import React, { Children, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import SwiperCore, { Mousewheel, Scrollbar } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useMediaQuery } from 'react-responsive';

import 'swiper/swiper-bundle.min.css';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import useIsBrowser from 'hooks/useIsBrowser';

// Registers Swiper modules. Not React's `use()` hook, which the rule matches
// by name.
// eslint-disable-next-line react-hooks/rules-of-hooks
SwiperCore.use([Mousewheel, Scrollbar]);

const CARDS_PER_VIEW_XS = 1.35;
const CARDS_PER_VIEW_SM = 2.15;
const CARDS_PER_VIEW_MD = 3.1;
const CARDS_PER_VIEW_LG = 4;

const CARDS_PER_VIEW_MINI_XS = 1.8;
const CARDS_PER_VIEW_MINI_SM = 2.75;
const CARDS_PER_VIEW_MINI_MD = 3.75;
const CARDS_PER_VIEW_MINI_LG = 5;

const SPACE_BETWEEN_CARDS_SM = 14;
const SPACE_BETWEEN_CARDS_MD = 24;

// A drag, wheel burst or glide keeps moving the slides until it settles
const NAVIGATE_SETTLE_MS = 150;

// Swiper event props that call onNavigate once per move the user made.
// Swiper's own updates (resize, breakpoints) and taps on a card don't count.
const useSwiperNavigateEvents = onNavigate => {
  const settleTimerRef = useRef(0);
  const settledTranslateRef = useRef(0);
  const isPressedRef = useRef(false);
  const isUserMoveRef = useRef(false);

  useEffect(() => () => clearTimeout(settleTimerRef.current), []);

  if (!onNavigate) {
    return {};
  }

  const scheduleSettle = swiper => {
    clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => {
      // Still dragging: the release schedules the settle again
      if (isPressedRef.current) {
        return;
      }

      const hasMoved =
        Math.abs(swiper.translate - settledTranslateRef.current) > 1;

      settledTranslateRef.current = swiper.translate;

      if (hasMoved && isUserMoveRef.current) {
        onNavigate();
      }

      isUserMoveRef.current = false;
    }, NAVIGATE_SETTLE_MS);
  };

  const press = () => {
    isPressedRef.current = true;
    isUserMoveRef.current = true;
  };

  const release = swiper => {
    isPressedRef.current = false;
    scheduleSettle(swiper);
  };

  return {
    onTouchStart: press,
    onTouchEnd: release,
    onScrollbarDragStart: press,
    onScrollbarDragEnd: release,
    // The mousewheel module's event
    onScroll: () => {
      isUserMoveRef.current = true;
    },
    onSetTranslate: scheduleSettle,
  };
};

const CardSlider = ({
  children,
  size = 'standard',
  title,
  wide = true,
  onNavigate,
}) => {
  const isBrowser = useIsBrowser();
  const navigateEvents = useSwiperNavigateEvents(onNavigate);
  // In React 18, it is critical to render identical HTML output during the
  // initial render in both browser and server environments.
  const renderDefault = !isBrowser;

  const isLargeMobileMin = useMediaQuery({
    query: '(min-width: 640px)',
  });

  const isTabletMin = useMediaQuery({
    query: '(min-width: 768px)',
  });

  const isDesktopMin = useMediaQuery({
    query: '(min-width: 1024px)',
  });

  const slidesPerView = useMemo(() => {
    if (isDesktopMin && !renderDefault) {
      return size === 'mini' ? CARDS_PER_VIEW_MINI_LG : CARDS_PER_VIEW_LG;
    }

    if (isTabletMin && !renderDefault) {
      return size === 'mini' ? CARDS_PER_VIEW_MINI_MD : CARDS_PER_VIEW_MD;
    }

    if (isLargeMobileMin && !renderDefault) {
      return size === 'mini' ? CARDS_PER_VIEW_MINI_SM : CARDS_PER_VIEW_SM;
    }

    return size === 'mini' ? CARDS_PER_VIEW_MINI_XS : CARDS_PER_VIEW_XS;
  }, [isDesktopMin, isLargeMobileMin, isTabletMin, renderDefault, size]);

  const spaceBetween =
    isTabletMin && !renderDefault
      ? SPACE_BETWEEN_CARDS_MD
      : SPACE_BETWEEN_CARDS_SM;

  const numberOfCards = Children.count(children);

  const isSliderRequired = useMemo(() => {
    if (isDesktopMin && !renderDefault) {
      return numberOfCards >= 5;
    }

    if (isTabletMin && !renderDefault) {
      return numberOfCards >= 4;
    }

    if (isLargeMobileMin && !renderDefault) {
      return numberOfCards >= 3;
    }

    return numberOfCards >= 2;
  }, [
    isDesktopMin,
    isLargeMobileMin,
    isTabletMin,
    numberOfCards,
    renderDefault,
  ]);

  return (
    <div className="max-w-[100vw] overflow-hidden">
      {!!title && (
        <FixedWidthContainer wide={wide}>
          <h4 className="text-xl font-bold text-gray mt-3 mx-0 mb-[7px]">
            {title}
          </h4>
        </FixedWidthContainer>
      )}

      <FixedWidthContainer
        wide={wide}
        className={classNames({
          // In new consumer app, if not on a small mobile (the default) then
          // hide the slider until the component has mounted to prevent a flash
          // of the incorrect layout.
          'min-[640px]:hidden': renderDefault,
        })}
      >
        {isSliderRequired ? (
          <Swiper
            freeMode
            mousewheel={{
              forceToAxis: true,
            }}
            scrollbar={{ draggable: true }}
            slidesPerView={slidesPerView}
            spaceBetween={spaceBetween}
            className="overflow-visible pb-4"
            {...navigateEvents}
          >
            {Children.map(children, item => (
              <SwiperSlide className="pt-[5px]">{item}</SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className="flex">
            {Children.map(children, item => (
              <div
                className={classNames(
                  'pt-[5px] flex-1 ml-3.5 first:ml-0 md:ml-6',
                  {
                    'max-w-[74.07407%] sm:max-w-[calc((100%_-_14px)_/2.15)] md:max-w-[calc((100%_-_48px)_/3.1)] lg:max-w-[calc((100%_-_72px)_/4)]':
                      size !== 'mini',
                    'max-w-[55.55556%] sm:max-w-[calc((100%_-_14px)_/2.75)] md:max-w-[calc((100%_-_48px)_/3.75)] lg:max-w-[calc((100%_-_72px)_/5)]':
                      size === 'mini',
                  }
                )}
              >
                {item}
              </div>
            ))}
          </div>
        )}
      </FixedWidthContainer>
    </div>
  );
};

CardSlider.propTypes = {
  children: PropTypes.node,
  size: PropTypes.oneOf(['mini', 'standard']),
  title: PropTypes.string,
  wide: PropTypes.bool,
  // Called once per drag, swipe, wheel or scrollbar move, after it settles
  onNavigate: PropTypes.func,
};

export default CardSlider;
